import {useEffect, useRef, useState} from 'react'
import {toast} from 'react-toastify'
import {caronaApi, SUGESTOES_ALTERNATIVAS_TIMEOUT_MS} from '../../../services/CaronaApi.service'
import {AtaDetalhe, AtaResumo, ItemComparado, SugestaoAlternativa} from '../types'
import {brl} from '../ui'

/** Prefixa "Ata " ao número só quando ele ainda não começa com "Ata" — o PNCP às
 * vezes já traz "Ata nº 00002/2026", e concatenar "Ata " cegamente gerava o rótulo
 * duplicado "Ata Ata nº 00002/2026". */
function rotuloNumeroAta(numero: string): string {
    const n = (numero || '').trim()
    return /^ata\b/i.test(n) ? n : `Ata ${n}`
}

/** Rótulo canônico de uma alternativa (ata): "Ata nº X — Órgão (UF)". */
function rotuloAlternativa(a: AtaResumo): string {
    return `${rotuloNumeroAta(a.numero_ata || a.numero_controle_pncp)} — ${a.orgao}${a.uf ? ` (${a.uf})` : ''}`
}

/** Rótulo do valor UNITÁRIO do item exato cotejado ("R$ X/UN"). "" quando não há
 * preço (o quadro degrada para "sem preço comparável"). */
function valorUnitarioTexto(it: ItemComparado | null | undefined): string {
    if (!it || it.valor_unitario == null) return ''
    const un = (it.unidade || '').trim() ? `/${it.unidade}` : ''
    return `${brl(it.valor_unitario)}${un}`
}

type SeloCmp = 'barata' | 'cara' | 'igual' | 'incomparavel'

/** Compara o preço UNITÁRIO NORMALIZADO (R$ por unidade_base) de uma alternativa
 * com o da ata escolhida — só na MESMA unidade-base (comparação justa; item-a-item).
 * Espelha o sinal determinístico do backend (Acórdão 547/2026-TCU, § 73): a decisão é
 * do gestor, este selo é só apoio. `incomparavel` = falta preço ou base diferente. */
export function compararUnitario(
    alt: ItemComparado | null | undefined,
    esc: ItemComparado | null | undefined
): SeloCmp {
    if (!alt || !esc) return 'incomparavel'
    const a = alt.valor_unitario_normalizado
    const e = esc.valor_unitario_normalizado
    if (a == null || e == null) return 'incomparavel'
    if ((alt.unidade_base || '') !== (esc.unidade_base || '')) return 'incomparavel'
    if (a < e) return 'barata'
    if (a > e) return 'cara'
    return 'igual'
}

const TIMEOUT_SEGUNDOS = Math.round(SUGESTOES_ALTERNATIVAS_TIMEOUT_MS / 1000)

/** O erro foi ESTOURO DE TEMPO (a chamada não voltou dentro do timeout)? O axios
 * sinaliza com code ECONNABORTED/ETIMEDOUT e sem `response`. Distinguir importa: a
 * mensagem para o gestor é outra ("demorou demais, tente de novo"). */
function ehEstouroDeTempo(err: any): boolean {
    if (err?.response) return false
    const code = err?.code
    return code === 'ECONNABORTED' || code === 'ETIMEDOUT' || /timeout/i.test(err?.message || '')
}

/** Segundos decorridos desde que `ativo` virou true (volta a 0 quando termina). */
function useSegundosDecorridos(ativo: boolean): number {
    const [segundos, setSegundos] = useState(0)
    useEffect(() => {
        setSegundos(0)
        if (!ativo) return
        const id = setInterval(() => setSegundos((s) => s + 1), 1000)
        return () => clearInterval(id)
    }, [ativo])
    return segundos
}

/** Indicador de PROGRESSO da chamada on-demand: barra determinística (fração do teto
 * de ~26s do backend, travada em 95%) + segundos decorridos. */
function ProgressoIA({segundos, rotulo}: {segundos: number; rotulo: string}) {
    const pct = Math.min(95, Math.round((segundos / TIMEOUT_SEGUNDOS) * 100))
    return (
        <div style={{display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0 0'}}>
            <div
                role="progressbar"
                aria-label={rotulo}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={pct}
                style={{flex: 1, maxWidth: 260, height: 6, borderRadius: 3, background: 'var(--border-subtle)', overflow: 'hidden'}}
            >
                <div style={{width: `${pct}%`, height: '100%', background: 'var(--brand-primary)', transition: 'width .4s linear'}} />
            </div>
            <span style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)'}}>
                {rotulo} — {segundos}s de até {TIMEOUT_SEGUNDOS}s
            </span>
        </div>
    )
}

interface Props {
    ata: AtaDetalhe
    termoBuscado: string
    /** Outras atas do mesmo objeto que a busca trouxe. */
    alternativas: AtaResumo[]
    /** Troca a ata ESCOLHIDA por uma alternativa mais vantajosa (o CaronaManager
     * carrega o detalhe da nova ata preservando o fluxo). Opcional (testes). */
    onAdotarAta?: (numeroControle: string) => void
    /** Abre o DETALHE de uma ata — fallback do "conferir item" sem link_pncp. */
    onAbrirDetalhe?: (numeroControle: string) => void
}

/** Comparação OPCIONAL com outras atas do mesmo objeto, ANTES de gerar (Acórdão
 * 547/2026-TCU, § 73): o gestor seleciona quais comparar, vê o item exato + preço
 * unitário com selo "Mais barata/cara" e pode ADOTAR a mais vantajosa. Reusa o
 * endpoint on-demand de sugestões (item_comparado normalizado por unidade-base). */
export default function ComparacaoAtas({ata, termoBuscado, alternativas, onAdotarAta, onAbrirDetalhe}: Props) {
    const alternativasExibidas = (alternativas || []).slice(0, 6)
    const controlesExibidos = alternativasExibidas
        .map((a) => a.numero_controle_pncp)
        .filter((c): c is string => Boolean(c))
    const altKey = controlesExibidos.join('|')

    const [selecionadas, setSelecionadas] = useState<Set<string>>(() => new Set(controlesExibidos))
    const [comparacao, setComparacao] = useState<SugestaoAlternativa[] | null>(null)
    const [comparando, setComparando] = useState(false)
    const [erroComparacao, setErroComparacao] = useState<string | null>(null)
    const segundosComparacao = useSegundosDecorridos(comparando)
    // Chave (controles ordenados) do que foi efetivamente comparado — detecta que a
    // seleção mudou desde a última comparação (mostra "atualize a comparação").
    const [comparadoKey, setComparadoKey] = useState('')
    // A comparação foi ANCORADA na busca original? Só então item_comparado=null
    // significa "não localizado nesta ata" (contrato do backend).
    const [comparadoComTermo, setComparadoComTermo] = useState(false)
    // Contexto corrente (escolhida + conjunto de alternativas): resposta de uma
    // comparação disparada num contexto ANTERIOR (ex.: antes de adotar outra ata)
    // é descartada — senão o quadro renderiza a nova escolhida sobre números da
    // antiga (e pode até alertar "mais barata" contra a ata que o gestor deixou).
    const contextoKey = `${ata.numero_controle_pncp}|${altKey}`
    const contextoRef = useRef(contextoKey)

    // link_pncp por numero_controle — alvo do "conferir item" clicável.
    const linkPncpPorControle = new Map<string, string>()
    for (const a of alternativasExibidas) {
        if (a.link_pncp) linkPncpPorControle.set(a.numero_controle_pncp, a.link_pncp)
    }

    // Troca de ata escolhida OU novo conjunto de alternativas: reseta a seleção e
    // descarta a comparação (e o erro) obsoletos.
    useEffect(() => {
        contextoRef.current = contextoKey
        setSelecionadas(new Set(controlesExibidos))
        setComparacao(null)
        setComparadoKey('')
        setComparadoComTermo(false)
        setErroComparacao(null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [altKey, ata.numero_controle_pncp])

    const toggleSelecionada = (controle: string) =>
        setSelecionadas((prev) => {
            const next = new Set(prev)
            if (next.has(controle)) next.delete(controle)
            else next.add(controle)
            return next
        })

    /** Chama o endpoint on-demand com a escolhida + as SELECIONADAS e guarda a
     * resposta. O cálculo de vantajosidade é do backend; a tela só exibe. */
    const compararSelecionadas = async () => {
        if (comparando) return
        const controles = controlesExibidos.filter((c) => selecionadas.has(c))
        if (controles.length === 0) {
            toast.info('Selecione ao menos uma outra ata para comparar.')
            return
        }
        setComparando(true)
        setErroComparacao(null)
        // Âncora do casamento: a BUSCA ORIGINAL do usuário. Com ela, o backend só
        // devolve item_comparado do item que CASA com a busca.
        const termoAncora = (termoBuscado || '').trim()
        // Carimbo do contexto no DISPARO: se a escolhida/alternativas mudarem
        // enquanto a chamada voa (ex.: "Adotar esta ata"), a resposta é obsoleta.
        const chaveDisparo = contextoRef.current
        try {
            const itens = await caronaApi.sugestoesAlternativas({
                ata_numero_controle: ata.numero_controle_pncp,
                alternativas: controles,
                ...(termoAncora ? {termo: termoAncora} : {}),
            })
            if (contextoRef.current !== chaveDisparo) return // contexto trocou: descarta
            setComparacao(itens)
            setComparadoComTermo(Boolean(termoAncora))
            setComparadoKey([...controles].sort().join('|'))
            if (itens.length === 0) {
                toast.info('A Licito.Guru IA não encontrou itens comparáveis para estas atas.')
            }
        } catch (err: any) {
            if (contextoRef.current !== chaveDisparo) return // contexto trocou: erro obsoleto
            const status = err?.response?.status
            const msg = ehEstouroDeTempo(err)
                ? `A comparação passou de ${TIMEOUT_SEGUNDOS} segundos e a espera foi encerrada — ` +
                  'nada foi cobrado. Tente novamente; se repetir, compare menos atas de uma vez.'
                : status === 404
                ? 'Não foi possível comparar: a ata escolhida não está mais disponível — refaça a busca.'
                : 'Não foi possível comparar os preços agora. Tente novamente em instantes.'
            setErroComparacao(msg)
            toast.error(msg)
        } finally {
            setComparando(false)
        }
    }

    /** Adota uma alternativa como a nova ata escolhida — decisão do gestor, então
     * CONFIRMA antes. */
    const adotarAlternativa = (s: SugestaoAlternativa) => {
        if (!onAdotarAta || !s.numero_controle) return
        const ok = window.confirm(
            `Adotar a Ata ${s.numero_ata || s.numero_controle}${s.orgao ? ` (${s.orgao})` : ''} como a ata escolhida?\n\n` +
            'O fluxo continua com a nova ata; as quantidades por item serão redefinidas ' +
            '(os itens da nova ata podem ser outros). A comparação será recalculada.'
        )
        if (ok) onAdotarAta(s.numero_controle)
    }

    // Derivados da comparação (a escolhida vem com eh_escolhida=true).
    const escolhidaCmp = (comparacao || []).find((s) => s.eh_escolhida) || null
    const escItem = escolhidaCmp?.item_comparado ?? null
    const alternativasCmpSel = (comparacao || [])
        .filter((s) => !s.eh_escolhida && selecionadas.has(s.numero_controle))
    const maisBaratas = alternativasCmpSel
        .filter((s) => compararUnitario(s.item_comparado, escItem) === 'barata')
    const maisVantajosa = maisBaratas.length
        ? maisBaratas.reduce((a, b) =>
            (a.item_comparado!.valor_unitario_normalizado ?? Infinity) <=
            (b.item_comparado!.valor_unitario_normalizado ?? Infinity) ? a : b)
        : null
    const selecaoDivergente = comparacao != null &&
        Array.from(selecionadas).filter((c) => controlesExibidos.includes(c)).sort().join('|') !== comparadoKey

    if (alternativasExibidas.length === 0) {
        return (
            <p style={{fontSize: 'var(--fs-sm)', color: 'var(--text-tertiary)', margin: 0}}>
                A busca não trouxe outras atas do mesmo objeto para comparar.
            </p>
        )
    }

    return (
        <div style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)'}}>
            <div style={{marginBottom: 6}}>
                Outras atas do mesmo objeto encontradas na busca — <strong>selecione as que quer comparar</strong> com a ata escolhida (item exato e preço unitário):
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: 4, margin: '0 0 8px'}}>
                {alternativasExibidas.map((a) => (
                    <label
                        key={a.numero_controle_pncp}
                        style={{display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer'}}
                    >
                        <input
                            type="checkbox"
                            style={{marginTop: 3}}
                            checked={selecionadas.has(a.numero_controle_pncp)}
                            onChange={() => toggleSelecionada(a.numero_controle_pncp)}
                            aria-label={`Comparar ${rotuloAlternativa(a)}`}
                        />
                        <span>
                            {rotuloNumeroAta(a.numero_ata || a.numero_controle_pncp)} — {a.orgao}
                            {a.uf ? ` (${a.uf})` : ''} · {a.selo_rotulo}
                        </span>
                    </label>
                ))}
            </div>
            <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10}}>
                <button
                    type="button"
                    className="lg-btn lg-btn--brand lg-btn--sm"
                    onClick={compararSelecionadas}
                    disabled={comparando || controlesExibidos.filter((c) => selecionadas.has(c)).length === 0}
                    aria-busy={comparando}
                    title={`Compara o item exato e o preço unitário das atas selecionadas com a ata escolhida (pode levar até ${TIMEOUT_SEGUNDOS}s).`}
                >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>
                    {comparando ? 'Comparando preços (Licito.Guru IA)…' : 'Comparar preços das selecionadas'}
                </button>
            </div>
            {comparando && (
                <ProgressoIA segundos={segundosComparacao} rotulo="Comparando preços" />
            )}
            {erroComparacao && !comparando && (
                <div className="callout callout--danger" role="alert" style={{margin: '10px 0 0'}}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <div>{erroComparacao}</div>
                </div>
            )}

            {comparacao != null && (
                <div style={{margin: '12px 0 2px'}}>
                    {selecaoDivergente && (
                        <div className="callout callout--info" style={{margin: '0 0 10px'}}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                            <div>Você mudou a seleção — clique em <strong>Comparar preços das selecionadas</strong> para atualizar o quadro abaixo.</div>
                        </div>
                    )}
                    {maisBaratas.length > 0 && (
                        <div className="callout callout--warn" role="alert" style={{margin: '0 0 10px'}}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                            <div style={{flex: 1}}>
                                <span className="callout__title">
                                    Atenção: {maisBaratas.length === 1 ? 'há uma ata mais barata' : `há ${maisBaratas.length} atas mais baratas`} por unidade
                                </span>
                                Considere adotar a alternativa mais vantajosa — a decisão é sua; os demais critérios do § 73 (condições, logística, prazos) também pesam.
                                {maisVantajosa && onAdotarAta && (
                                    <div style={{marginTop: 8}}>
                                        <button
                                            type="button"
                                            className="lg-btn lg-btn--brand lg-btn--sm"
                                            onClick={() => adotarAlternativa(maisVantajosa)}
                                        >
                                            Adotar a mais vantajosa (Ata {maisVantajosa.numero_ata || maisVantajosa.numero_controle})
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {escItem && (
                        <div style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)', marginBottom: 6}}>
                            Ata escolhida ({rotuloNumeroAta(ata.numero_ata || ata.numero_controle_pncp)}):{' '}
                            <strong>{valorUnitarioTexto(escItem) || 'sem preço comparável'}</strong>
                            {escItem.descricao ? ` · ${escItem.descricao}` : ''}
                        </div>
                    )}
                    {alternativasCmpSel.length === 0 ? (
                        <div style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)'}}>
                            Nenhuma ata selecionada na comparação.
                        </div>
                    ) : (
                        <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                            {alternativasCmpSel.map((s) => {
                                const it = s.item_comparado
                                // Comparação ancorada na busca + item null = o backend NÃO
                                // localizou o item correspondente nesta ata. Card neutro.
                                const naoLocalizado = comparadoComTermo && it == null
                                const cmp = naoLocalizado ? 'incomparavel' : compararUnitario(it, escItem)
                                const valor = valorUnitarioTexto(it)
                                const selo =
                                    cmp === 'barata' ? {txt: 'Mais barata', cls: 'callout--danger'} :
                                    cmp === 'cara' ? {txt: 'Mais cara', cls: 'callout--success'} :
                                    cmp === 'igual' ? {txt: 'Mesmo preço', cls: 'callout--info'} :
                                    null
                                const linkPncp = linkPncpPorControle.get(s.numero_controle)
                                const tituloConferir = 'O item exato cotejado não pôde ser confirmado automaticamente — confira antes de decidir.'
                                return (
                                    <div
                                        key={s.numero_controle}
                                        style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--border-subtle)'}}
                                    >
                                        <div style={{flex: 1, minWidth: 200}}>
                                            <div style={{fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' as any, color: 'var(--text-primary)'}}>
                                                {rotuloNumeroAta(s.numero_ata || s.numero_controle)}{s.orgao ? ` — ${s.orgao}` : ''}{s.uf ? ` (${s.uf})` : ''}
                                            </div>
                                            <div style={{fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)'}}>
                                                {naoLocalizado
                                                    ? 'Item correspondente não localizado nesta ata'
                                                    : it && (valor || it.descricao)
                                                    ? <>{valor && <strong>{valor}</strong>}{valor && it.descricao ? ' · ' : ''}{it.descricao || ''}</>
                                                    : 'sem preço comparável'}
                                                {!naoLocalizado && s.item_comparado_incerto && (
                                                    linkPncp ? (
                                                        <a
                                                            href={linkPncp}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title={tituloConferir}
                                                            style={{marginLeft: 6, color: 'var(--feedback-warning)', whiteSpace: 'nowrap', textDecoration: 'underline', cursor: 'pointer'}}
                                                        >
                                                            · conferir item
                                                        </a>
                                                    ) : onAbrirDetalhe ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => onAbrirDetalhe(s.numero_controle)}
                                                            title={tituloConferir}
                                                            style={{marginLeft: 6, color: 'var(--feedback-warning)', whiteSpace: 'nowrap', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', padding: 0, font: 'inherit'}}
                                                        >
                                                            · conferir item
                                                        </button>
                                                    ) : (
                                                        <span
                                                            title={tituloConferir}
                                                            style={{marginLeft: 6, color: 'var(--feedback-warning)', whiteSpace: 'nowrap'}}
                                                        >
                                                            · conferir item
                                                        </span>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                        {selo && (
                                            <span className={`callout ${selo.cls}`} style={{margin: 0, padding: '2px 8px', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-medium)' as any}}>
                                                {selo.txt}
                                            </span>
                                        )}
                                        {cmp === 'barata' && onAdotarAta && (
                                            <button type="button" className="lg-btn lg-btn--secondary lg-btn--sm" onClick={() => adotarAlternativa(s)}>
                                                Adotar esta ata
                                            </button>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                    <p style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)', margin: '8px 0 0'}}>
                        Comparação por preço unitário na mesma unidade-base (item exato de cada ata). A Licito.Guru IA é apoio — confira os dados antes de decidir.
                    </p>
                </div>
            )}
        </div>
    )
}
